import { ModalConfirm } from '@/components/element/modal/ModalConfirm';
import { MotionDiv } from '@/components/element/MotionDiv';
import { SimpleTextFormatWithLink } from '@/components/element/SimpleTextFormat';
import { UrlCopyButton } from '@/components/element/UrlCopyButton';
import { useQuotationTasksInvalidate } from '@/hooks/api/1440_quotation_tasks';
import { useDestroyQuotationTask } from '@/hooks/api/1470_destroy_quotation_task';
import { useQuotationTaskInvalidate } from '@/hooks/api/1480_quotation_task';
import { useUnarchiveQuotationTask } from '@/hooks/api/1484_unarchive_quotation_tasks';
import { useCreateQuotationTaskSubscription } from '@/hooks/api/1510_create_quotation_task_subscription';
import { useDestroyQuotationTaskSubscription } from '@/hooks/api/1520_destroy_quotation_task_subscription';
import { toastApiError, toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentQuotationTask } from '@/providers/QuotationTaskProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { dateFormat, isApprovalURL, isBackofficeURL } from '@/utils';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

export const TaskSummary = () => {
  const location = useLocation();
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { quotationTask: task } = useCurrentQuotationTask();
  const [editPagePath, setEditPagePath] = useState<string>('');
  const [subscribed, setSubscribed] = useState<boolean>(() => {
    return task!.quotation_task_subscriptions.some((d) => d.user.id == me!.id);
  });

  useEffect(() => {
    if (isBackofficeURL(location.pathname)) {
      setEditPagePath(
        (_) =>
          `/w/${_w_!.identifier}/backoffice/teams/${_t_!.id}/sales_orders/${sales_order_id}/quotation_tasks/${
            task!.id
          }/edit`
      );
    } else if (isApprovalURL(location.pathname)) {
      setEditPagePath(
        (_) =>
          `/w/${_w_!.identifier}/approval/teams/${_t_!.id}/sales_orders/${sales_order_id}/quotation_tasks/${
            task!.id
          }/edit`
      );
    } else {
      setEditPagePath(
        (_) => `/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${sales_order_id}/quotation_tasks/${task!.id}/edit`
      );
    }
  }, [location.pathname]);

  return (
    <MotionDiv className="card mb-4">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div>
          <h6 className="mb-0">タスク基本情報</h6>
        </div>
        <div className="d-flex align-items-center">
          <UrlCopyButton
            url={`${process.env.REACT_APP_SITE_URL}/w/${_w_!.identifier}/teams/${
              _t_!.id
            }/sales_orders/${sales_order_id}/quotation_tasks/${task!.id}`}
          />
          <Link to={editPagePath} className="btn btn-outline-success btn-icon-only">
            <i className="fas fa-edit"></i>
          </Link>
          <div className="dropstart ms-3">
            <a href="" className="text-secondary" id="dropdownMarketingCard" data-bs-toggle="dropdown">
              <i className="fas fa-ellipsis-v"></i>
            </a>
            <ul className="dropdown-menu dropdown-menu-lg-start px-2 py-3" aria-labelledby="dropdownMarketingCard">
              <li>{task!.archived && <UnarchiveLink />}</li>
              <li>
                <RemoveTaskLink />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="card-body pt-0">
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">タスク名</div>
          <div className="font-weight-bold text-end px-0 text-sm text-dark">{task!.name}</div>
        </div>
        <hr className="horizontal dark" />
        {task!.archived && (
          <>
            <div className="d-flex align-items-center justify-content-between">
              <div className="text-secondary text-xs font-weight-bold pe-2">アーカイブ</div>
              <div className="font-weight-bold text-end px-0 text-sm text-dark">
                <span className="badge badge-danger">アーカイブ済</span>
              </div>
            </div>
            <hr className="horizontal dark" />
          </>
        )}
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">タスク担当者</div>
          <div className="d-flex align-items-center">
            <img src={task!.user.user_account.icon_file_url} className="avatar avatar-xs rounded-circle me-1" />
            <div className="font-weight-bold px-0 text-sm text-dark">{task!.user.user_account.name}</div>
          </div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">マイリスト登録ユーザー</div>
          <div className="d-flex align-items-center justify-content-center">
            <div className="avatar-group text-nowrap overflow-hidden flex-1">
              {task!.quotation_task_subscriptions.map((d) => (
                <img
                  key={`qts-${d.id}`}
                  src={d.user.user_account.icon_file_url}
                  className="avatar avatar-xs rounded-circle"
                />
              ))}
            </div>
            {subscribed ? (
              <UnSubscribeLink setSubscribed={setSubscribed}></UnSubscribeLink>
            ) : (
              <SubscribeLink setSubscribed={setSubscribed} />
            )}
          </div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">期限日</div>
          <div className="font-weight-bold text-end px-0 text-sm text-dark">
            {task!.due_date ? dateFormat({ date: task!.due_date }) : '−'}
          </div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">メモ</div>
          <div className="px-0 text-xs break-word">
            <SimpleTextFormatWithLink content={task!.description || ''} />
          </div>
        </div>
      </div>
    </MotionDiv>
  );
};

const RemoveTaskLink = () => {
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { quotationTask: task } = useCurrentQuotationTask();
  const { mutate, isPending, isSuccess, error } = useDestroyQuotationTask();
  const { invalidate } = useQuotationTasksInvalidate(_w_!.identifier);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess) {
      invalidate();
      toastSuccess('申請タスク(見積)を削除しました');
      navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${sales_order_id}`);
    }
  }, [isSuccess]);

  const onDeleteClick = () => {
    mutate({ identifier: _w_!.identifier, team_id: _t_!.id, sales_order_id: Number(sales_order_id), id: task!.id });
  };

  return (
    <button className="dropdown-item border-radius-md text-danger" onClick={() => onDeleteClick()}>
      <i className="fas fa-trash-alt me-2"></i>タスクの削除
    </button>
  );
};

type SubscriptionLinkProps = {
  setSubscribed: Dispatch<SetStateAction<boolean>>;
};

// 購読ボタン
const SubscribeLink = ({ setSubscribed }: SubscriptionLinkProps) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { quotationTask: task } = useCurrentQuotationTask();
  const { mutate, data, isPending, isSuccess, error } = useCreateQuotationTaskSubscription();

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (isSuccess && data) {
      setSubscribed((_) => true);
      task!.quotation_task_subscriptions.push(data);
      toastSuccess('追加しました');
    }
  }, [isSuccess]);

  useEffect(() => toastApiError(error), [error]);

  const onClick = () => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: Number(sales_order_id),
      task_id: task!.id,
    });
  };

  return (
    <>
      <ModalConfirm
        modalId={'subscribeConfirm'}
        okClicked={onClick}
        message={
          'この申請タスク(見積)をマイリストに追加しますか？\nマイリストに登録すると一覧画面で簡単に見つけられるようになります'
        }
      />

      <button
        data-bs-toggle="modal"
        data-bs-target={`#subscribeConfirm`}
        className="btn bg-gradient-dark btn-icon-only ms-2"
      >
        <i className="fas fa-rss"></i>
      </button>
    </>
  );
};

// 購読解除ボタン
const UnSubscribeLink = ({ setSubscribed }: SubscriptionLinkProps) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { quotationTask: task } = useCurrentQuotationTask();
  const { mutate, data, isPending, isSuccess, error } = useDestroyQuotationTaskSubscription();
  const subscription = task!.quotation_task_subscriptions.find((d) => d.user.id == me!.id);

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (isSuccess && data) {
      setSubscribed(false);
      task!.quotation_task_subscriptions = task!.quotation_task_subscriptions.filter((d) => d.id != subscription!.id);
      toastSuccess('解除しました');
    }
  }, [isSuccess]);

  useEffect(() => toastApiError(error), [error]);

  const onClick = () => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: Number(sales_order_id),
      task_id: task!.id,
      id: subscription!.id,
    });
  };

  return (
    <>
      <ModalConfirm
        modalId={'unSubscribeConfirm'}
        okClicked={onClick}
        message={'この申請タスク(見積)をマイリストから解除しますか？'}
      />

      <button
        data-bs-toggle="modal"
        data-bs-target={`#unSubscribeConfirm`}
        className="btn bg-gradient-danger btn-icon-only ms-2"
      >
        <i className="fas fa-rss"></i>
      </button>
    </>
  );
};

// アーカイブ解除ボタン
const UnarchiveLink = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { mutate, isPending, isSuccess, error } = useUnarchiveQuotationTask();
  const { sales_order_id } = useParams();
  const { quotationTask: task } = useCurrentQuotationTask();
  const { invalidate } = useQuotationTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id),
    id: task!.id,
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess) {
      toastSuccess('アーカイブを解除しました');
      invalidate();
    }
  }, [isSuccess]);

  const onArchiveClick = () => {
    mutate({ identifier: _w_!.identifier, team_id: _t_!.id, sales_order_id: Number(sales_order_id), id: task!.id });
  };

  return (
    <button className="dropdown-item border-radius-md" onClick={onArchiveClick}>
      <i className="fas fa-box-open me-2"></i>アーカイブを解除
    </button>
  );
};
